import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { chatApi } from '../../api/chat.api'
import { useSocket } from '../../socket/SocketProvider'
import type { ChatMessage } from '../../api/types'

const TYPING_EMIT_THROTTLE_MS = 2000
const TYPING_EXPIRY_MS = 4000

/** "Daniel schreibt…" / "Daniel und Lisa schreiben…" / "Daniel, Lisa und 2 weitere
 * schreiben…" — `null` wenn gerade niemand tippt. */
export function formatTypingLabel(names: string[]): string | null {
  if (names.length === 0) return null
  if (names.length === 1) return `${names[0]} schreibt…`
  if (names.length === 2) return `${names[0]} und ${names[1]} schreiben…`
  return `${names[0]}, ${names[1]} und ${names.length - 2} weitere schreiben…`
}

/**
 * Gemeinsame Chat-Zustandslogik (Channel-Liste, Nachrichten, Socket-Join/Leave/Read,
 * Senden/Bearbeiten/Löschen/Reagieren) — von der vollen Teamchat-Seite UND dem
 * schwebenden Chat-Widget genutzt, damit die (nicht ganz triviale) Socket-Verkabelung
 * nur an einer Stelle existiert.
 */
export function useChatSession() {
  const socket = useSocket()
  const queryClient = useQueryClient()

  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [typingUsers, setTypingUsers] = useState<Record<string, { displayName: string; expiresAt: number }>>({})
  const lastTypingEmitRef = useRef(0)

  const channelsQuery = useQuery({ queryKey: ['chat', 'channels'], queryFn: async () => (await chatApi.listChannels()).data })
  const unreadQuery = useQuery({ queryKey: ['chat', 'unread-counts'], queryFn: async () => (await chatApi.unreadCounts()).data, refetchInterval: 20_000 })

  useEffect(() => {
    if (!selectedChannelId && channelsQuery.data && channelsQuery.data.length > 0) {
      setSelectedChannelId(channelsQuery.data[0]!.id)
    }
  }, [channelsQuery.data, selectedChannelId])

  useEffect(() => {
    if (!selectedChannelId) return
    chatApi.listMessages(selectedChannelId).then((res) => setMessages(res.data))
    socket?.emit('chat:join', { channelId: selectedChannelId })
    socket?.emit('chat:read', { channelId: selectedChannelId })
    chatApi.markRead(selectedChannelId).then(() => queryClient.invalidateQueries({ queryKey: ['chat', 'unread-counts'] }))
    setTypingUsers({})

    return () => {
      socket?.emit('chat:leave', { channelId: selectedChannelId })
    }
  }, [selectedChannelId, socket, queryClient])

  // "Tippt gerade"-Anzeige: das Backend sendet chat:typing bewusst ohne eigenes
  // "hat aufgehört zu tippen"-Event (siehe chat.socket.ts) — der Eintrag läuft hier
  // stattdessen einfach nach TYPING_EXPIRY_MS ohne neues Event ab, geprüft per Interval.
  useEffect(() => {
    if (!socket) return
    const onTyping = ({ channelId, user }: { channelId: string; user: { id: string; displayName: string } }) => {
      if (channelId !== selectedChannelId) return
      setTypingUsers((prev) => ({ ...prev, [user.id]: { displayName: user.displayName, expiresAt: Date.now() + TYPING_EXPIRY_MS } }))
    }
    socket.on('chat:typing', onTyping)
    return () => {
      socket.off('chat:typing', onTyping)
    }
  }, [socket, selectedChannelId])

  useEffect(() => {
    const interval = setInterval(() => {
      setTypingUsers((prev) => {
        const now = Date.now()
        const entries = Object.entries(prev).filter(([, v]) => v.expiresAt > now)
        return entries.length === Object.keys(prev).length ? prev : Object.fromEntries(entries)
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!socket) return

    const onMessage = (message: ChatMessage) => {
      if (message.channelId === selectedChannelId) {
        setMessages((prev) => [...prev, message])
      } else {
        queryClient.invalidateQueries({ queryKey: ['chat', 'unread-counts'] })
      }
    }
    const onUpdated = (message: ChatMessage) => {
      setMessages((prev) => prev.map((m) => (m.id === message.id ? message : m)))
    }

    socket.on('chat:message', onMessage)
    socket.on('chat:message_updated', onUpdated)
    return () => {
      socket.off('chat:message', onMessage)
      socket.off('chat:message_updated', onUpdated)
    }
  }, [socket, selectedChannelId, queryClient])

  const sendMessage = (body: string, replyToId?: string) => {
    if (!selectedChannelId) return
    socket?.emit('chat:send', { channelId: selectedChannelId, body, replyToId })
  }

  const notifyTyping = () => {
    if (!selectedChannelId) return
    const now = Date.now()
    if (now - lastTypingEmitRef.current < TYPING_EMIT_THROTTLE_MS) return
    lastTypingEmitRef.current = now
    socket?.emit('chat:typing', { channelId: selectedChannelId })
  }

  const editMessage = (message: ChatMessage, body: string) => {
    socket?.emit('chat:edit', { channelId: message.channelId, messageId: message.id, body })
  }

  const deleteMessage = (message: ChatMessage) => {
    socket?.emit('chat:delete', { channelId: message.channelId, messageId: message.id })
  }

  const toggleReaction = (message: ChatMessage, emoji: string, currentUserId: string) => {
    const alreadyReacted = message.reactions.some((r) => r.userId === currentUserId && r.emoji === emoji)
    socket?.emit(alreadyReacted ? 'chat:unreact' : 'chat:react', { channelId: message.channelId, messageId: message.id, emoji })
  }

  return {
    channelsQuery,
    unreadQuery,
    selectedChannelId,
    setSelectedChannelId,
    messages,
    sendMessage,
    editMessage,
    deleteMessage,
    toggleReaction,
    notifyTyping,
    typingLabel: formatTypingLabel(Object.values(typingUsers).map((u) => u.displayName)),
  }
}
